import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@material-ui/core";
import DataGrid, {
  Paging,
  Column,
  Editing,
  Summary,
  TotalItem,
  RowDragging,
} from "devextreme-react/data-grid";
import {
  getUIData,
  getUIProperty,
  setUIData,
  setUIProperty,
} from "../../redux/slices/uiSlice.js";
import {
  getResourceAPI,
  postResource,
} from "../../redux/slices/resourceSlice.js";
import { handleNotificationMessage } from "../../utils/notifications.js";
import { useParams } from "react-router";
import { itemGridColumns } from "./config.js";
import { numberWithCommas, tabNames } from "../../utils/strings.js";
import notify from "devextreme/ui/notify";
import useResourceStatuses from "../../hooks/useResourceStatus.jsx";
import CustomSelection from "../generic/CustomSelection.jsx";
import { currenciesToSymbols } from "../../utils/general.js";
import InsertFromCsvModal from "./InsertFromCsvModal.jsx";

const canFillRest = (lineNumber) => {
  let infoMessage = "Successfully fill rest columns";
  let canFill = true;

  if (!isNaN(lineNumber)) {
    infoMessage = "Cannot fill rest files because lineNumber is not a header";
    canFill = false;
  }

  return [canFill, infoMessage];
};

export const onValueChanged = (gridRef, rowIndex, value, dataField) => {
  gridRef.current.instance.cellValue(rowIndex, dataField, value);
};

const clearNullData = (data) => {
  const convertedData = { ...data };
  Object.keys(convertedData).map((key) =>
    convertedData[key] === null ||
    convertedData[key] === undefined ||
    convertedData[key] === ""
      ? delete convertedData[key]
      : {}
  );

  return convertedData;
};

const TradeCaseItems = ({ setReady }) => {
  const dispatch = useDispatch();
  const { tabName } = useParams();
  const [canDrag, setCanDrag] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);

  const filteredItems = useSelector((state) =>
    getUIProperty(state, "filteredItems")
  );

  const allSelectedLineItems = useSelector((state) =>
    getUIProperty(state, "selectedLineItems")
  );

  const selectedLineItems = useMemo(() => {
    if (!filteredItems || !allSelectedLineItems || !filteredItems.length)
      return [];

    const currentSelectedLineItems =
      allSelectedLineItems[filteredItems[0].stage_id];

    if (currentSelectedLineItems instanceof Array) {
      return currentSelectedLineItems.filter((i) => i) || [];
    }
    return [];
  }, [allSelectedLineItems, filteredItems]);

  const selectedCurrency = useSelector((state) =>
    getUIProperty(state, "selectedCurrency")
  );

  const itemInfo = useSelector((state) => getUIData(state, "itemInfo"));
  const { vendorStatus } = useResourceStatuses();

  const selectedCell = useRef({});
  const gridRef = useRef(null);

  useEffect(() => {
    vendorStatus === "idle" && dispatch(getResourceAPI({ path: "vendor" }));
  }, [dispatch, vendorStatus]);

  const onSelectionChanged = useCallback(
    (selection) => {
      if (selection) {
        dispatch(
          setUIData({
            name: `selectedLineItems`,
            value: {
              [filteredItems[0].stage_id]: selection.selectedRowKeys || [],
            },
          })
        );
      }
    },
    [dispatch, filteredItems]
  );

  const onCellClick = useCallback(
    (cell) => {
      const { rowIndex } = cell;
      const { value, visibleIndex, dataField } = cell.column;
      selectedCell.current = { value, rowIndex, visibleIndex, dataField };
    },
    [selectedCell]
  );

  const fillRestColumns = useCallback(() => {
    if (!gridRef.current) return;

    const visibleRows = gridRef.current.instance.getVisibleRows();
    const { dataField, rowIndex } = selectedCell.current;

    if (!dataField || rowIndex == null) return;

    const [canFill, infoMessage] = canFillRest(
      visibleRows[rowIndex].data.line_number,
      dataField
    );

    if (!canFill) {
      notify(infoMessage, "error", 4000);
      return;
    }

    notify(infoMessage, "success", 4000);

    const value = visibleRows[rowIndex].data[dataField];

    let newRows = visibleRows.map((vr) => vr.data);
    for (let i = rowIndex + 1; i < newRows.length; i++) {
      if (isNaN(newRows[i].line_number)) break;
      else {
        gridRef.current.instance.cellValue(i, dataField, value);
      }
    }

    gridRef.current.instance.refresh(true);
  }, []);

  const forceOrder = useCallback(() => {
    const currentLineItems = [
      ...gridRef.current?.instance
        .getVisibleRows()
        .map((vr) => ({ ...vr.data })),
    ];

    const newLineItems = [];

    let newIndex = 1;

    for (const currentLineItem of currentLineItems) {
      if (isNaN(currentLineItem.line_number)) {
        newLineItems.push(currentLineItem);
        newIndex = 1;
      } else {
        newLineItems.push({ ...currentLineItem, line_number: newIndex });
        newIndex += 1;
      }
    }

    dispatch(setUIProperty({ name: "filteredItems", value: newLineItems }));
    gridRef.current?.instance.cancelEditData();
  }, [dispatch]);

  const handleSave = useCallback(async () => {
    setReady(false);

    const newLineItems = [
      ...gridRef.current?.instance
        .getVisibleRows()
        .map((vr) => ({ ...vr.data })),
    ];

    const { payload } = await dispatch(
      postResource({
        path: "line-items",
        data: {
          line_items: newLineItems.map((item) =>
              clearNullData({...item, ...itemInfo})
          ),
          stage_id: itemInfo.stage_id,
          stage_type: itemInfo.stage_type,
          trade_case_id: itemInfo.trade_case_id
        },
      })
    );

    dispatch(
      setUIProperty({ name: "filteredItems", value: payload.data.line_items })
    );

    gridRef.current?.instance.cancelEditData();

    if (payload.error)
      handleNotificationMessage(payload.path, "", payload.error, "error");

    setCanDrag(true);
    setReady(true);
  }, [dispatch, itemInfo, setReady]);

  const handleDelete = useCallback(async () => {
    const newLineItems = [...filteredItems].filter(
      (item) => !selectedLineItems.includes(item.id)
    );

    dispatch(setUIProperty({ name: "filteredItems", value: newLineItems }));

    const { payload } = await dispatch(
      postResource({
        path: "line-items",
        data: {
          line_items: newLineItems.map((item) =>
              clearNullData({...item, ...itemInfo})
          ),
          stage_id: filteredItems[0].stage_id,
          stage_type: filteredItems[0].stage_type,
          trade_case_id: filteredItems[0].trade_case_id
        },
      })
    );

    if (payload.error)
      handleNotificationMessage(payload.path, "", payload.error, "error");

    setCanDrag(true);
    setReady(true);
  }, [dispatch, filteredItems, itemInfo, selectedLineItems, setReady]);

  const onToolbarPreparing = useCallback(
    (e) => {
      const saveIndex = e.toolbarOptions.items.findIndex(
        (option) => option.name === "saveButton"
      );
      e.toolbarOptions.items.splice(saveIndex, 1);
      e.toolbarOptions.items.unshift(
        {
          location: "after",
          widget: "dxButton",
          options: {
            icon: "arrowdown",
            hint: "Fill rest",
            onClick: fillRestColumns,
          },
        },
        {
          location: "after",
          widget: "dxButton",
          options: {
            icon: "refresh",
            hint: "Apply order",
            onClick: forceOrder,
          },
        },
        {
          location: "after",
          widget: "dxButton",
          options: {
            icon: "xlsfile",
            hint: "Import from csv",
            onClick: () => setShowImportModal(true),
          },
        },
        {
          location: "after",
          widget: "dxButton",
          options: {
            icon: "trash",
            hint: "Delete",
            disabled: selectedLineItems.length === 0,
            onClick: () => handleDelete(),
          },
        }
      );

      e.toolbarOptions.items.push({
        location: "after",
        widget: "dxButton",
        options: {
          icon: "save",
          hint: "SAVE",
          onClick: () => handleSave(),
        },
      });
    },
    [
      fillRestColumns,
      forceOrder,
      handleDelete,
      handleSave,
      selectedLineItems.length,
    ]
  );

  const onReorder = useCallback(
    (e) => {
      const newLineItems = [
        ...e.component.getVisibleRows().map((vr) => ({ ...vr.data })),
      ];

      const toIndex = e.toIndex;
      const fromIndex = e.fromIndex;

      newLineItems.splice(fromIndex, 1);
      newLineItems.splice(toIndex, 0, e.itemData);

      dispatch(setUIProperty({ name: "filteredItems", value: newLineItems }));
    },
    [dispatch]
  );

  const onInitNewRow = useCallback(({ data }) => {
    data.id = Date.now();
    setCanDrag(false);
  }, []);

  return useMemo(
    () => (
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        {showImportModal && (
          <InsertFromCsvModal
            visible={showImportModal}
            setVisible={setShowImportModal}
            tabName={tabName}
          />
        )}
        <Grid item xs={12}>
          {filteredItems && (
            <DataGrid
              keyExpr="id"
              columnAutoWidth={true}
              id="gridContainer"
              rowAlternationEnabled={true}
              ref={gridRef}
              allowColumnResizing={true}
              dataSource={filteredItems}
              showBorders={true}
              selection={{ mode: "multiple" }}
              onSelectionChanged={onSelectionChanged}
              onToolbarPreparing={onToolbarPreparing}
              onCellClick={onCellClick}
              onInitNewRow={onInitNewRow}
            >
              <RowDragging
                allowReordering={canDrag}
                onReorder={onReorder}
                showDragIcons={true}
              />
              <Paging enabled={false} />
              <Editing
                mode="batch"
                allowUpdating={true}
                allowAdding={true}
                allowDeleting={false}
                allowSorting={false}
              />

              {(
                itemGridColumns[tabName || tabNames[0]] ||
                itemGridColumns[tabNames[0]]
              ).map((column, idx) =>
                column.type === "select" ? (
                  <Column
                    key={column.dataField}
                    dataField={column.dataField}
                    caption={column.caption}
                    // width={column.width}
                    editCellRender={(cell) => {
                      return (
                        <CustomSelection
                          valueExpr={column.valueExpr}
                          resourceName={column.resourceName}
                          resourcePath={column.resourcePath}
                          dataSource={column.dataSource}
                          value={cell.data[column.dataField]}
                          handleChange={(e) =>
                            onValueChanged(
                              gridRef,
                              cell.rowIndex,
                              e.value,
                              column.dataField
                            )
                          }
                        />
                      );
                    }}
                  />
                ) : (
                  <Column
                    key={idx}
                    cellRender={column.cellRender}
                    dataField={column.dataField}
                    caption={column.caption}
                    // width={column.width}
                    allowEditing={column.allowEditing}
                    allowSorting={false}
                    calculateCellValue={column.calculateCellValue}
                  />
                )
              )}

              <Summary
                calculateCustomSummary={(options) => {
                  if (options.name === "averageCommission") {
                    if (options.summaryProcess === "start") {
                      options.totalValue = 0;
                      options.length = 0;
                    } else if (options.summaryProcess === "calculate") {
                      options.length += options.value.quantity;
                      options.totalValue +=
                        options.value.quantity * options.value.commission;
                    } else if (options.summaryProcess === "finalize") {
                      options.totalValue = options.totalValue / options.length;
                    }
                  }

                  if (options.name === "customSum") {
                    if (options.summaryProcess === "start") {
                      options.totalValue = 0;
                    } else if (options.summaryProcess === "calculate") {
                      let value = options.value;
                      if (value.length > 0) {
                        value = value.replaceAll(",", "");
                      }
                      options.totalValue += parseFloat(value);
                    }
                  }
                }}
              >
                <TotalItem
                  column="totalValue"
                  name="customSum"
                  summaryType="custom"
                  customizeText={(data) =>
                    `${numberWithCommas(data.value)}${
                      currenciesToSymbols[selectedCurrency] || "$"
                    }`
                  }
                />
                <TotalItem
                  column="quantity"
                  summaryType="sum"
                  customizeText={(data) => `${data.value}`}
                />
                <TotalItem
                  name="averageCommission"
                  summaryType="custom"
                  displayFormat="Sum: {0}"
                  showInColumn="commission"
                  customizeText={(data) => numberWithCommas(data.value)}
                />
              </Summary>
            </DataGrid>
          )}
        </Grid>
      </Grid>
    ),
    [
      showImportModal,
      filteredItems,
      onSelectionChanged,
      onToolbarPreparing,
      onCellClick,
      onInitNewRow,
      canDrag,
      onReorder,
      tabName,
      selectedCurrency,
    ]
  );
};
export default React.memo(TradeCaseItems);
